import {
  cardCopyResultSchema,
  cardTemplateListSchema,
  cardTemplateSchema,
  createCardFromTemplateBodySchema,
  type ApiError,
  type CardCopyResult,
  type CardTemplate,
  type CardTemplateList,
  type CreateCardFromTemplateBody,
} from '@molt/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationResult,
  type UseQueryResult,
  type QueryClient,
} from '@tanstack/react-query';
import { announce } from './announce';
import { del, get, post } from './api';
import { applyCopiedCardToBoardContents } from './card-copy';
import { setBoardContentsQueries } from './cross-board-move';
import { useInterfaceLanguage } from './interface-language';
import { queryKeys } from './query-keys';

export interface CreateCardFromTemplateVariables {
  readonly boardId: string;
  readonly templateCardId: string;
  readonly templateTitle: string;
  readonly destinationListId: string;
  readonly previousCardId: string | null;
  readonly nextCardId: string | null;
  readonly title: string;
}

export function getCardTemplate(_cardId: string): Promise<CardTemplate> {
  return get(`/cards/${encodeURIComponent(_cardId)}/template`, cardTemplateSchema);
}

export function markCardTemplate(_cardId: string): Promise<CardTemplate> {
  return post(`/cards/${encodeURIComponent(_cardId)}/template`, cardTemplateSchema);
}

export function unmarkCardTemplate(_cardId: string): Promise<CardTemplate> {
  return del(`/cards/${encodeURIComponent(_cardId)}/template`, cardTemplateSchema);
}

export function hideCardTemplateFromList(_cardId: string): Promise<CardTemplate> {
  return post(
    `/cards/${encodeURIComponent(_cardId)}/template/hidden-from-list`,
    cardTemplateSchema,
  );
}

export function unhideCardTemplateFromList(_cardId: string): Promise<CardTemplate> {
  return del(
    `/cards/${encodeURIComponent(_cardId)}/template/hidden-from-list`,
    cardTemplateSchema,
  );
}

export function getBoardCardTemplates(_boardId: string): Promise<CardTemplateList> {
  return get(
    `/boards/${encodeURIComponent(_boardId)}/card-templates`,
    cardTemplateListSchema,
  );
}

export function createCardFromTemplate(
  _cardId: string,
  _body: CreateCardFromTemplateBody,
): Promise<CardCopyResult> {
  return post(
    `/cards/${encodeURIComponent(_cardId)}/create-from-template`,
    cardCopyResultSchema,
    createCardFromTemplateBodySchema.parse(_body),
  );
}

export function writeCardTemplateSuccess(
  queryClient: QueryClient,
  boardId: string,
  template: CardTemplate,
): void {
  queryClient.setQueryData(queryKeys.cards.template(template.id), template);
  void queryClient.invalidateQueries({ queryKey: queryKeys.cards.detail(template.id) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.contents(boardId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.lists(boardId) });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.boards.cardTemplates(boardId),
  });
}

export function cardTemplateQueryOptions(
  cardId: string,
): UseQueryOptions<CardTemplate, ApiError> {
  return {
    queryKey: queryKeys.cards.template(cardId),
    queryFn: () => getCardTemplate(cardId),
  };
}

export function boardCardTemplatesQueryOptions(
  boardId: string,
): UseQueryOptions<CardTemplateList, ApiError> {
  return {
    queryKey: queryKeys.boards.cardTemplates(boardId),
    queryFn: () => getBoardCardTemplates(boardId),
  };
}

export function useCardTemplate(
  cardId: string,
  enabled: boolean,
): UseQueryResult<CardTemplate, ApiError> {
  return useQuery({
    ...cardTemplateQueryOptions(cardId),
    enabled,
  });
}

export function useBoardCardTemplates(
  boardId: string,
  enabled: boolean,
): UseQueryResult<CardTemplateList, ApiError> {
  return useQuery({
    ...boardCardTemplatesQueryOptions(boardId),
    enabled,
  });
}

export function useMarkCardTemplate(
  boardId: string,
): UseMutationResult<
  CardTemplate,
  ApiError,
  { readonly cardId: string; readonly title: string }
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: ({ cardId }) => markCardTemplate(cardId),
    onSuccess: (template) => {
      writeCardTemplateSuccess(queryClient, boardId, template);
    },
    onError: (_error, variables) => {
      announce(t('cardTemplateHooks.markFailed', { title: variables.title }));
    },
  });
}

export function useUnmarkCardTemplate(
  boardId: string,
): UseMutationResult<
  CardTemplate,
  ApiError,
  { readonly cardId: string; readonly title: string }
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: ({ cardId }) => unmarkCardTemplate(cardId),
    onSuccess: (template) => {
      writeCardTemplateSuccess(queryClient, boardId, template);
    },
    onError: (_error, variables) => {
      announce(t('cardTemplateHooks.unmarkFailed', { title: variables.title }));
    },
  });
}

export function useHideCardTemplateFromList(
  boardId: string,
): UseMutationResult<
  CardTemplate,
  ApiError,
  { readonly cardId: string; readonly title: string }
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: ({ cardId }) => hideCardTemplateFromList(cardId),
    onSuccess: (template) => {
      writeCardTemplateSuccess(queryClient, boardId, template);
    },
    onError: (_error, variables) => {
      announce(t('cardTemplateHooks.hideFailed', { title: variables.title }));
    },
  });
}

export function useUnhideCardTemplateFromList(
  boardId: string,
): UseMutationResult<
  CardTemplate,
  ApiError,
  { readonly cardId: string; readonly title: string }
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: ({ cardId }) => unhideCardTemplateFromList(cardId),
    onSuccess: (template) => {
      writeCardTemplateSuccess(queryClient, boardId, template);
    },
    onError: (_error, variables) => {
      announce(t('cardTemplateHooks.unhideFailed', { title: variables.title }));
    },
  });
}

export function useCreateCardFromTemplate(
  boardId: string,
): UseMutationResult<CardCopyResult, ApiError, CreateCardFromTemplateVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables: CreateCardFromTemplateVariables) =>
      createCardFromTemplate(variables.templateCardId, {
        listId: variables.destinationListId,
        previousCardId: variables.previousCardId,
        nextCardId: variables.nextCardId,
        title: variables.title,
      }),
    onSuccess: (result, variables) => {
      queryClient.setQueryData(queryKeys.cards.detail(result.card.id), result.card);
      setBoardContentsQueries(queryClient, boardId, (previous) =>
        applyCopiedCardToBoardContents(
          previous,
          result.card,
          variables.previousCardId,
          variables.nextCardId,
        ),
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.lists(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.capacity(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.detail(result.card.id),
      });
    },
    onError: (_error, variables) => {
      announce(t('cardTemplateHooks.createFailed', { title: variables.templateTitle }));
    },
  });
}
